import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useAuth } from '../hooks/useSupabaseAuth.jsx'
import { X } from 'lucide-react'

const AuthModal = ({ isOpen, onClose }) => {
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const { signInWithGoogle } = useAuth()

  const resetForm = () => {
    setError('')
    setSuccess('')
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleGoogleSignIn = async () => {
    setIsLoading(true)
    setError('')
    setSuccess('')

    try {
      const { data, error } = await signInWithGoogle()

      if (error) {
        console.error('Google Sign-in error details:', error)
        
        // Pesan error yang lebih spesifik berdasarkan jenis error
        if (error.message?.includes('popup')) {
          setError('Popup login diblokir. Mohon izinkan popup atau gunakan metode login lain.')
        } else if (error.message?.includes('network')) {
          setError('Masalah koneksi internet. Periksa koneksi Anda dan coba lagi.')
        } else if (error.message?.includes('consent')) {
          setError('Izin akses ditolak. Mohon berikan izin yang diperlukan untuk login.')
        } else if (error.message?.includes('configuration')) {
          setError('Masalah konfigurasi OAuth. Mohon hubungi administrator.')
        } else {
          setError(error.message || 'Google Sign-in gagal. Silakan coba lagi.')
        }
      } else if (!data) {
        // Jika tidak ada error tapi juga tidak ada data, mungkin popup ditutup
        setError('Login dibatalkan atau popup ditutup. Silakan coba lagi.')
      }
      // Note: For OAuth, the redirect will handle the success case
    } catch (err) {
      console.error('Unexpected error during Google sign-in:', err)
      setError('Terjadi kesalahan tak terduga. Silakan coba lagi nanti.')
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-bg-primary/80 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-in fade-in-0 duration-300">
      <div className="relative bg-gradient-to-br from-bg-secondary/95 to-bg-quaternary/95 backdrop-blur-xl rounded-3xl shadow-2xl w-full max-w-md border border-border-primary/30 overflow-hidden transform animate-in zoom-in-95 duration-300">
        {/* Decorative gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-brand-primary/5 via-transparent to-brand-secondary/5 pointer-events-none" />
        
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-6 right-6 p-2 hover:bg-bg-tertiary rounded-full transition-all duration-200 group z-10"
        >
          <X className="w-5 h-5 text-text-tertiary group-hover:text-text-primary transition-colors" />
        </button>
        
        {/* Content */}
        <div className="relative p-8 pt-12">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <img 
                src="https://media.cara.id/images/logo-cara-white.png" 
                alt="Cara Logo" 
                className="w-12 h-12 object-contain"
              />
              {/* Subtle glow effect */}
              <div className="absolute inset-0 bg-brand-primary/20 blur-xl rounded-full scale-150 -z-10" />
            </div>
          </div>

          {/* Welcome Message */}
          <div className="text-center space-y-4 mb-8">
            <h2 className="text-2xl font-bold text-text-primary">
              Selamat Datang
            </h2>
            <p className="text-text-secondary text-sm leading-relaxed max-w-xs mx-auto">
              Masuk untuk mengakses semua fitur AI yang tersedia dan mulai berkreasi
            </p>
          </div>

          {/* Google Sign-in Button */}
          <div className="space-y-4">
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-gray-800 font-medium py-4 px-6 rounded-xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-200 group transform hover:scale-[1.02]"
            >
              <svg className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="text-sm font-medium">
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-gray-700 rounded-full animate-spin" />
                    Menghubungkan...
                  </div>
                ) : (
                  'Lanjutkan dengan Google'
                )}
              </span>
            </button>
            
            {/* Error Message */}
            {error && (
              <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl backdrop-blur-sm animate-in slide-in-from-top-2 duration-300">
                <p className="text-red-400 text-sm text-center font-medium">{error}</p>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl backdrop-blur-sm animate-in slide-in-from-top-2 duration-300">
                <p className="text-green-400 text-sm text-center font-medium">{success}</p>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="text-center mt-8 pt-6 border-t border-border-primary">
            <p className="text-text-tertiary text-xs leading-relaxed">
              Dengan masuk, Anda menyetujui syarat dan ketentuan kami
            </p>
          </div>
        </div>
        
        {/* Bottom decorative element */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-brand-primary via-brand-secondary to-brand-primary" />
      </div>
    </div>
  )
}

AuthModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
}

export default AuthModal